import _ from 'lodash'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogActions from '@mui/material/DialogActions'
import IconButton from '@mui/material/IconButton'
import Portal from '@mui/material/Portal'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import VitalParametersIcon from '@/components/ui/icons/VitalParameters'

import useMedconnectDevices from '@/hooks/useMedconnectDevices'
import { useRequestHandler } from '@/hooks/useRequestHandler'

import { useDiagnosticLayout } from '@/layouts/diagnostic/DiagnosticLayout'

import { ConsultationType, ConsultationStatus } from '@/core/models/consultation.model'
import { Room } from '@/core/models/room.model'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import {
  clearNewlyCreatedConsultationId,
  createConsultation,
  endConsultation,
  getConsultation,
  getStunServer,
  joinConsultationSocketEvent,
  joinMeeting,
  leaveConsultationSocketEvent,
  selectAttendeeInfo,
  selectConsultationById,
  selectConsultationCreateRequestTracker,
  selectConsultationUpdateRequestTracker,
  selectMeetingInfo,
  selectNewlyCreatedConsultationId,
  selectRpiMeetingId,
  selectTwilioStunServer,
  updateConsultation,
} from '@/redux/reducers/consultation.reducer'
import { getCustomDevices, selectCustomDevices } from '@/redux/reducers/custom-device.reducer'
import {
  addUserToRoom,
  createRoom,
  enterEmergencyRoomSocketEvent,
  getRoom,
  leaveEmergencyRoomSocketEvent,
  selectPeerId,
  selectRoomByConsultationId,
  selectRoomById,
  updateRoom,
} from '@/redux/reducers/room.reducer'

import Documents from '@/pages/diagnostic/containers/Documents'
import EmergencyPatientSelection from '@/pages/diagnostic/containers/EmergencyPatientSelection'
import InvitationDialog from '@/pages/diagnostic/containers/InvitationDialog'
import { getEnv } from '@/utils/helpers'
import { isStaff } from '@/utils/type-guards'
import MobileConsultation from '@/pages/diagnostic/containers/MobileConsultation'
import MedicalVideoIcon from '@/components/ui/icons/MedicalVideo'
import Biotech from '@mui/icons-material/Biotech'
import StethoscopeIcon from '@/components/ui/icons/Stethoscope'
import { MobileEmergencyTopbar } from '@/pages/diagnostic/containers/MobileEmergencyTopbar'
import { Visio } from '@/pages/diagnostic/containers/Visio'

dayjs.extend(duration)

export function MobileEmergencyPage() {
  const dispatch = useAppDispatch()
  const { topbarContentContainer } = useDiagnosticLayout()
  const navigate = useNavigate()
  const { roomId } = useParams()

  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)
  const emergencyRoom = useAppSelector((state) => {
    if (!authUser || !isStaff(authUser)) return null

    if (authUser.status === 'MEDECIN') return roomId ? selectRoomById(state, roomId) : null

    return authUser.emergencyRoom
      ? selectRoomById(state, typeof authUser.emergencyRoom === 'string' ? authUser.emergencyRoom : authUser.emergencyRoom._id)
      : null
  })
  const emergencyConsultationId = useMemo(() => emergencyRoom?.currentConsultation, [emergencyRoom])
  const emergencyConsultation = useAppSelector((state) => (emergencyConsultationId ? selectConsultationById(state, emergencyConsultationId) : undefined))
  const consultationRoom = useAppSelector((state) => (emergencyConsultationId ? selectRoomByConsultationId(state, emergencyConsultationId) : undefined))
  const newlyCreatedConsultationId = useAppSelector(selectNewlyCreatedConsultationId)
  const medicalVideoAttendeeInfo = useAppSelector((state) => selectAttendeeInfo(state, 'medicalVideo'))
  const medicalVideoMeetingInfo = useAppSelector((state) => selectMeetingInfo(state, 'medicalVideo'))
  const rpiMeetingId = useAppSelector(selectRpiMeetingId)
  const stethoscopeAttendeeInfo = useAppSelector((state) => selectAttendeeInfo(state, 'stethoscope'))
  const stethoscopeMeetingInfo = useAppSelector((state) => selectMeetingInfo(state, 'stethoscope'))
  const twilioStunServer = useAppSelector(selectTwilioStunServer)
  const consultationCreateRequestTracker = useAppSelector(selectConsultationCreateRequestTracker)
  const customDevices = useAppSelector(selectCustomDevices)
  const peerId = useAppSelector(selectPeerId)

  const [openInvitationDialog, setOpenInvitationDialog] = useState<boolean>(false)
  const [openDocumentsDialog, setOpenDocumentsDialog] = useState<boolean>(false)
  const [openLeavingRoomAlertDialog, setOpenLeavingRoomAlertDialog] = useState<boolean>(false)
  const [isInfo, setIsInfo] = useState(true)

  useMedconnectDevices(isStaff(authUser) ? authUser.status === 'PDS' : false, emergencyConsultationId ? emergencyConsultationId : '')

  const endConsultationRequestHandler = useRequestHandler({
    onSuccess: () => {
      if (!emergencyRoom) return

      if (openLeavingRoomAlertDialog) {
        dispatch(updateRoom(emergencyRoom._id, { currentConsultation: null }))
        navigate('/')
      } else {
        dispatch(updateRoom(emergencyRoom._id, { currentConsultation: null, status: 'available' }))
      }
    },
    onError: () => {
      enqueueSnackbar(t('notification.consultation.update.error'), { variant: 'error' })
    },
    requestTrackerSelector: selectConsultationUpdateRequestTracker,
  })

  const doctorLeavingRoomRequestHandler = useRequestHandler({
    onSuccess: () => {
      navigate('/emergencies')
    },
    onError: () => {
      enqueueSnackbar(t('notification.consultation.update.error'), { variant: 'error' })
    },
    requestTrackerSelector: selectConsultationUpdateRequestTracker,
  })

  const handleJoinMeeting = useCallback(() => {
    if (!authUser || !isStaff(authUser)) return

    if (authUser.status === 'MEDECIN' && !!roomId) {
      dispatch(joinMeeting({ roomOrConsultationId: roomId, userId: authUser._id, meetingType: 'visioConference' }))
      return
    }

    if (authUser.status === 'PDS' && !!authUser.emergencyRoom) {
      dispatch(
        joinMeeting({
          roomOrConsultationId: typeof authUser.emergencyRoom === 'string' ? authUser.emergencyRoom : authUser.emergencyRoom._id,
          userId: authUser._id,
          meetingType: 'visioConference',
        })
      )
      return
    }
  }, [dispatch, authUser, roomId])

  const peerConfig = useMemo(
    () =>
      !twilioStunServer
        ? undefined
        : {
          iceServers: twilioStunServer.iceServers,
        },
    [twilioStunServer]
  )
  const mainRender = () => {
    if (!authUser) return null

    if (consultationCreateRequestTracker.loading)
      return (
        <Box sx={{ height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress size="24px" color="info" />
        </Box>
      )

    if (!emergencyRoom?.currentConsultation) {
      return isStaff(authUser) && authUser.status === 'PDS' ? (
        <Stack alignItems="center" justifyContent="center" sx={{ width: '100%' }}>
          <EmergencyPatientSelection onSelect={handleSelectPatient} />
        </Stack>
      ) : (
        <Stack>
          <Alert severity="info">{t('page.emergency.message.waitingForPatientSelection')}</Alert>
        </Stack>
      )
    }

    if (!consultationRoom) return null

    const rpiConfig = !rpiMeetingId
      ? undefined
      : {
        meetingID: rpiMeetingId,
        serverUrl: getEnv('VITE_RPI_GATEWAY')!,
      }

    return (
      <>{isInfo ? <MobileConsultation
        consultationId={emergencyConsultation?._id}
        menuItems={sidebarMenuItems}
        stethoscopeAttendeeInfo={stethoscopeAttendeeInfo}
        stethoscopeMeetingInfo={stethoscopeMeetingInfo}
        audioPeerjsConfig={{
          config: peerConfig,
          peerId: peerId + 'audio',
          streamer: consultationRoom.streamer + 'audio',
        }}
        videoPeerjsConfig={{
          config: peerConfig,
          peerId: peerId + 'video',
          streamer: consultationRoom.streamer + 'video',
        }}
        rpiConfig={rpiConfig}
        customDevices={customDevices}
        medicalVideoAttendeeInfo={medicalVideoAttendeeInfo}
        medicalVideoMeetingInfo={medicalVideoMeetingInfo}
      /> :
        <Visio consultation={emergencyConsultation} handleJoinMeeting={handleJoinMeeting} />
      }</>
    )
  }

  const sidebarMenuItems = useMemo(
    () => [
      {
        id: 'mesures',
        icon: VitalParametersIcon,
        label: t('page.localConsultation.sidebar.mesures.label'),
      },
      {
        id: 'medicalVideo',
        icon: MedicalVideoIcon,
        label: t('page.liveConsultation.sidebar.medicalVideo.label'),
      },
      {
        id: 'biologicals',
        icon: Biotech,
        label: t('page.localConsultation.sidebar.biologicals.label'),
      },
      {
        id: 'stethoscope',
        icon: StethoscopeIcon,
        label: t('page.localConsultation.sidebar.stethoscope.label'),
        isNested: true,
      },
    ],
    [t]
  )

  const handleLeaveRoom = () => {
    if (!authUser || !emergencyRoom || !isStaff(authUser)) return

    if (authUser.status === 'PDS') {
      if (emergencyRoom.currentConsultation) {
        setOpenLeavingRoomAlertDialog(true)
        return
      }

      navigate('/')
      return
    }

    if (authUser.status === 'MEDECIN') {
      if (emergencyRoom.currentConsultation) {
        doctorLeavingRoomRequestHandler.dispatch(updateConsultation(emergencyRoom.currentConsultation, { medecin: null }))
        return
      }

      navigate('/emergencies')
      return
    }
  }

  const handleFinishAndLeave = () => {
    if (!emergencyRoom || !emergencyRoom.currentConsultation) return

    endConsultationRequestHandler.dispatch(endConsultation(emergencyRoom.currentConsultation))
  }

  const handleSelectPatient = useCallback(
    (patientId: string) => {
      if (!authUser) return

      dispatch(
        createConsultation({
          consultation: {
            patient: patientId,
            pds: authUser._id,
            startDate: new Date().toISOString(),
            type: ConsultationType.Emergency,
            status: ConsultationStatus.Active,
          },
        })
      )
    },
    [dispatch, authUser]
  )

  const toggleDocumentsDialog = () => {
    setOpenDocumentsDialog(!openDocumentsDialog)
  }

  const toggleInvitationDialog = () => {
    setOpenInvitationDialog(!openInvitationDialog)
  }

  const toggleLeavingRoomAlertDialog = () => {
    setOpenLeavingRoomAlertDialog(!openLeavingRoomAlertDialog)
  }

  useEffect(() => {
    dispatch(clearNewlyCreatedConsultationId()) // make sure there is no newly created consultation when page is mounted
    dispatch(getStunServer())
    dispatch(getCustomDevices())
    return () => {
      // make sure there is no newly created consultation when page is unmounted
      dispatch(clearNewlyCreatedConsultationId())
    }
  }, [dispatch])

  // get PDS's emergency room or create one if it is not existed
  useEffect(() => {
    if (!authUser || !isStaff(authUser) || authUser.status !== 'PDS') return

    if (!authUser.emergencyRoom) {
      dispatch(createRoom({ creator: authUser._id, status: 'available' }, true))
      return
    }

    dispatch(getRoom(typeof authUser.emergencyRoom === 'string' ? authUser.emergencyRoom : authUser.emergencyRoom._id))
  }, [dispatch, authUser])

  // get PDS's emergency room via roomId param in url: /emergencies/:roomId
  // usually it's doctor who enters the room
  useEffect(() => {
    if (!roomId) return

    dispatch(getRoom(roomId))
    dispatch(enterEmergencyRoomSocketEvent(roomId))

    return () => {
      dispatch(leaveEmergencyRoomSocketEvent(roomId))
    }
  }, [dispatch, roomId])

  // retrieve the correct status of emergency room when pds comebacks to the room
  useEffect(() => {
    if (!isStaff(authUser) || authUser?.status !== 'PDS' || !emergencyRoom) return

    let status: Room['status']

    if (emergencyRoom.status === 'inactive' && !!emergencyRoom.currentConsultation) status = 'standby'

    if (emergencyRoom.status !== 'available' && !emergencyRoom.currentConsultation) status = 'available'

    if (!!status) dispatch(updateRoom(emergencyRoom._id, { status }))
  }, [authUser, dispatch, emergencyRoom])

  useEffect(() => {
    if (!isStaff(authUser) || authUser?.status !== 'PDS' || !emergencyRoom || !emergencyConsultation) return

    // the status of emergency room becomes in progress when doctor joins the consultation
    if (emergencyRoom.status === 'standby' && !!emergencyConsultation.medecin) {
      dispatch(updateRoom(emergencyRoom._id, { status: 'in_progress' }))
    }

    // the status of emergency room becomes standby when doctor leaves the consultation
    if (emergencyRoom.status === 'in_progress' && !emergencyConsultation.medecin) {
      dispatch(updateRoom(emergencyRoom._id, { status: 'standby' }))
    }
  }, [authUser, dispatch, emergencyConsultation, emergencyRoom])

  // get emergency consultation
  useEffect(() => {
    if (!emergencyConsultationId) return

    if (!emergencyConsultation) dispatch(getConsultation(emergencyConsultationId))
    dispatch(joinConsultationSocketEvent(emergencyConsultationId))

    return () => {
      dispatch(leaveConsultationSocketEvent(emergencyConsultationId))
    }
  }, [dispatch, emergencyConsultationId, emergencyConsultation])

  // get consultation room
  useEffect(() => {
    if (!!consultationRoom || !emergencyRoom?.currentConsultation) return

    dispatch(getRoom(emergencyRoom.currentConsultation))
  }, [consultationRoom, dispatch, emergencyRoom])

  // set current consultation of emergency room when a consultation is created successfully on the side of pds
  useEffect(() => {
    if (!isStaff(authUser) || authUser?.status !== 'PDS' || !newlyCreatedConsultationId || !emergencyRoom || !!emergencyRoom.currentConsultation) return

    dispatch(updateRoom(emergencyRoom._id, { currentConsultation: newlyCreatedConsultationId, status: 'standby' }))
  }, [authUser, dispatch, emergencyRoom, newlyCreatedConsultationId])

  useEffect(() => {
    if (
      !consultationRoom ||
      !authUser ||
      authUser._id === (typeof consultationRoom.creator === 'string' ? consultationRoom.creator : consultationRoom.creator._id) ||
      !emergencyRoom?.currentConsultation
    ) {
      return
    }

    dispatch(addUserToRoom(emergencyRoom.currentConsultation))
  }, [authUser, consultationRoom, dispatch, emergencyRoom])

  useEffect(() => {
    return () => {
      if (!!authUser && isStaff(authUser) && authUser.status === 'PDS' && !!authUser.emergencyRoom) {
        const emergencyRoomId = typeof authUser.emergencyRoom === 'string' ? authUser.emergencyRoom : authUser.emergencyRoom._id
        dispatch(updateRoom(emergencyRoomId, { status: 'inactive' }))
      }
    }
  }, [authUser, dispatch])

  if (!emergencyRoom || !isStaff(authUser)) return null

  if (authUser?.status !== 'PDS' && emergencyRoom.status === 'inactive') {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', overflow: 'hidden' }}>
        <Portal container={topbarContentContainer}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}></Stack>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Tooltip title={t('page.emergency.leaveRoomButton.tooltip')}>
                <IconButton onClick={handleLeaveRoom}>
                  <ExitToAppIcon htmlColor="#FFF" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Portal>
        <Typography>{t('page.emergency.message.pdsNotInRoom')}</Typography>
      </Stack>
    )
  }

  return (
    <Stack direction="row" sx={{ height: '100%', overflow: 'hidden' }}>
      <MobileEmergencyTopbar
        authUser={authUser}
        consultation={emergencyConsultation}
        roomCreator={emergencyRoom.creator}
        handleLeaveRoom={handleLeaveRoom}
        toggleInvitationDialog={toggleInvitationDialog}
        isInfo={isInfo}
        setIsInfo={setIsInfo}
      />
      {mainRender()}
      {!!emergencyRoom.currentConsultation && (
        <Dialog open={openDocumentsDialog} onClose={toggleDocumentsDialog}>
          <Documents consultationId={emergencyRoom.currentConsultation} />
        </Dialog>
      )}
      <Dialog
        open={openLeavingRoomAlertDialog}
        onClose={toggleLeavingRoomAlertDialog}
        PaperProps={{
          sx: {
            minWidth: '350px',
          },
        }}
      >
        <DialogTitle>
          {t('page.emergency.leavingRoomAlertDialog.title')}
          <IconButton
            onClick={toggleLeavingRoomAlertDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button color="primary" onClick={handleFinishAndLeave} variant="contained">
            {t('page.emergency.leavingRoomAlertDialog.finishConsultationAndLeaveRoomButton.label')}
          </Button>
          <Button color="error" component={Link} to="/" variant="contained">
            {t('page.emergency.leavingRoomAlertDialog.leaveRoomButton.label')}
          </Button>
        </DialogActions>
      </Dialog>
      <InvitationDialog open={openInvitationDialog} onClose={toggleInvitationDialog} roomId={emergencyRoom._id} />
    </Stack>
  )
}

export default MobileEmergencyPage
